import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useWsNavigate } from "../hooks/useWsNavigate";
import {
  ChevronRight,
  CheckCircle2,
  XCircle,
  Clock,
  BarChart2,
  ArrowLeft,
  Bot,
  StopCircle,
  RotateCcw,
  Play,
  ChevronDown,
} from "lucide-react";
import { scenariosAPI, metricsAPI } from "../services/api";
import { usePageTitle } from "../contexts/PageTitleContext";
import { useAuth } from "../contexts/AuthContext";
import StatusBadge from "../components/ui/StatusBadge";
import ConversationTypeBadge from "../components/ConversationTypeBadge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { SearchInput } from "../components/ui/search-input";
import Pagination from "../components/ui/Pagination";

const formatDuration = (seconds) => {
  if (!seconds) return "—";
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

const avgDuration = (runs) => {
  const completed = runs.filter((r) => r.duration);
  if (!completed.length) return "—";
  const avg = completed.reduce((sum, r) => sum + r.duration, 0) / completed.length;
  return formatDuration(Math.round(avg));
};

// Passed/total across every evaluated metric (numeric audio + verdict metrics),
// computed server-side in RunSerializer#metrics_summary so numeric measurements
// count toward the total instead of only the pass/fail LLM metrics.
const getMetricsSummary = (run) => {
  const summary = run?.metrics_summary;
  if (!summary || !summary.total) return null;
  return { passed: summary.passed, total: summary.total };
};

const BatchRunDetails = () => {
  const { setPageTitle } = usePageTitle();
  const { id } = useParams();
  const navigate = useWsNavigate();
  const { user, currentWorkspace } = useAuth();
  const [batch, setBatch] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [selectedRunIds, setSelectedRunIds] = useState(new Set());
  const [allMetrics, setAllMetrics] = useState([]);
  const [rerunMetricsOpen, setRerunMetricsOpen] = useState(false);
  const [rerunMetricMode, setRerunMetricMode] = useState("menu"); // "menu" | "select"
  const [rerunMetricIds, setRerunMetricIds] = useState([]);

  useEffect(() => {
    if (batch) setPageTitle(batch.name);
    return () => setPageTitle(null);
  }, [batch, setPageTitle]);

  useEffect(() => {
    if (user && currentWorkspace && id) {
      fetchBatchDetails();
      fetchRuns();
      fetchMetrics();
    }
  }, [user, currentWorkspace, id]);

  // Auto-refresh while there are running/pending runs
  useEffect(() => {
    const hasActive = runs.some((r) => r.status === "running" || r.status === "pending");
    if (!hasActive) return;
    const timer = setInterval(() => {
      fetchBatchDetails(true);
      fetchRuns();
    }, 5000);
    return () => clearInterval(timer);
  }, [runs]);

  const fetchBatchDetails = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await scenariosAPI.getBatch(id, currentWorkspace.id);
      if (response?.data) setBatch(response.data);
    } catch (err) {
      if (!silent) setError("Failed to fetch batch details");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchRuns = async () => {
    try {
      const response = await scenariosAPI.getBatchRuns(id, currentWorkspace.id);
      if (response?.data) setRuns(response.data);
    } catch (err) {
      console.error("Error fetching runs:", err);
    }
  };

  const filteredRuns = runs.filter((run) => {
    const matchesSearch =
      !searchTerm ||
      run.scenario_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      run.id.toString().includes(searchTerm);
    const matchesStatus = filterStatus === "all" || run.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Client-side pagination of the table. All runs stay fetched so the summary
  // counts, avg duration, auto-refresh and bulk actions still see the whole
  // batch — only the rendered rows are paged (a stress-test batch can have
  // hundreds of runs).
  const RUNS_PER_PAGE = 25;
  const totalPages = Math.max(1, Math.ceil(filteredRuns.length / RUNS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * RUNS_PER_PAGE;
  const pagedRuns = filteredRuns.slice(pageStart, pageStart + RUNS_PER_PAGE);

  // Whenever the filter/search narrows the list, jump back to page 1 so the
  // user isn't stranded on an out-of-range page.
  useEffect(() => {
    setPage(1);
  }, [searchTerm, filterStatus]);

  const completedRuns = runs.filter((r) => r.status === "completed").length;
  const failedRuns    = runs.filter((r) => r.status === "failed").length;
  const runningRuns   = runs.filter((r) => r.status === "running").length;

  // When every failed run in the batch shares the same error, it's almost
  // always one root cause (a bad agent phone number, a missing web URL, no
  // credits) rather than N unrelated failures. Surface it once at the top so
  // the user isn't left reading "failed" on every row with no explanation.
  const sharedFailureReason = (() => {
    const failed = runs.filter((r) => r.status === "failed");
    if (failed.length < 2 || runningRuns > 0) return null;
    const messages = new Set(failed.map((r) => r.error_message).filter(Boolean));
    if (messages.size !== 1) return null;
    const wholeBatch = failed.length === runs.length;
    return { count: failed.length, wholeBatch, message: [...messages][0] };
  })();

  const handleStopRun = async (e, run) => {
    e.stopPropagation();
    if (!window.confirm(`Stop run #${run.id}? This will terminate the active call.`)) return;
    try {
      await scenariosAPI.stopRun(id, run.id);
      setRuns((prev) => prev.map((r) => r.id === run.id ? { ...r, status: "failed" } : r));
    } catch (err) {
      alert("Failed to stop run: " + (err.response?.data?.error || err.message));
    }
  };

  const handleRerun = async (e, run) => {
    e.stopPropagation();
    try {
      await scenariosAPI.rerunRun(id, run.id);
      await fetchRuns();
    } catch (err) {
      alert("Failed to rerun: " + (err.response?.data?.error || err.message));
    }
  };

  const handleBulkRerun = async () => {
    if (selectedRunIds.size === 0) return;
    try {
      await Promise.all([...selectedRunIds].map((runId) => scenariosAPI.rerunRun(id, runId)));
      setSelectedRunIds(new Set());
      await fetchRuns();
    } catch (err) {
      alert("Failed to rerun selected: " + (err.response?.data?.error || err.message));
    }
  };

  const handleBatchRerun = async () => {
    const activeCount = runs.filter((r) => r.status === "running" || r.status === "pending").length;
    const msg = activeCount > 0
      ? `This will re-queue all ${runs.length} runs. ${activeCount} are still running and will be queued after they finish. Continue?`
      : `This will retrigger all ${runs.length} run${runs.length !== 1 ? "s" : ""}. Continue?`;
    if (!window.confirm(msg)) return;
    try {
      await scenariosAPI.rerunBatch(id);
      await fetchBatchDetails();
      await fetchRuns();
    } catch (err) {
      alert("Failed to rerun batch: " + (err.response?.data?.error || err.message));
    }
  };

  const fetchMetrics = async () => {
    try {
      const mRes = await metricsAPI.getAllMetrics(currentWorkspace.id, { per_page: 999 });
      const standard = mRes?.standard_metrics || [];
      const custom = mRes?.data || [];
      const global = mRes?.global_metrics || [];
      setAllMetrics([...standard, ...custom, ...global]);
    } catch {}
  };

  const closeRerunMetrics = () => {
    setRerunMetricsOpen(false);
    setRerunMetricMode("menu");
  };

  const toggleRerunMetric = (metricId) => {
    setRerunMetricIds((prev) =>
      prev.includes(metricId) ? prev.filter((x) => x !== metricId) : [...prev, metricId]
    );
  };

  // Metric rerun targets the selected runs, or all runs when none are selected.
  const metricRerunTargets = () =>
    selectedRunIds.size > 0 ? [...selectedRunIds] : runs.map((r) => r.id);

  const handleRerunMetrics = async (metricIds = null) => {
    const targets = metricRerunTargets();
    if (targets.length === 0) return;
    closeRerunMetrics();
    try {
      await Promise.all(targets.map((runId) => scenariosAPI.rerunMetrics(id, runId, metricIds)));
      if (metricIds) setRerunMetricIds([]);
      await fetchRuns();
    } catch (err) {
      alert("Failed to rerun metrics: " + (err.response?.data?.error || err.message));
    }
  };

  const toggleSelectRun = (runId) => {
    setSelectedRunIds((prev) => {
      const next = new Set(prev);
      next.has(runId) ? next.delete(runId) : next.add(runId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const eligibleIds = filteredRuns
      .filter((r) => r.status !== "running" && r.status !== "pending")
      .map((r) => r.id);
    const allSelected = eligibleIds.every((id) => selectedRunIds.has(id));
    setSelectedRunIds(allSelected ? new Set() : new Set(eligibleIds));
  };

  if (!user) return null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground" />
      </div>
    );
  }

  if (error) {
    return <div className="px-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-4">{error}</div>;
  }

  if (!batch) {
    return <div className="px-4 text-sm text-muted-foreground">Batch not found.</div>;
  }

  return (
    <div className="px-4">
      {/* Back nav */}
      <button
        onClick={() => navigate("/batch-run")}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Batch Runs
      </button>

      {/* Batch Header Card */}
      <div className="bg-card rounded-xl border border-border shadow-sm p-4 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-lg font-semibold text-foreground">{batch.name}</h1>
            {batch.agent_name && (
              <div className="flex items-center gap-1.5 mt-1.5 text-sm text-muted-foreground">
                <Bot className="h-4 w-4" />
                <span>{batch.agent_name}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {runningRuns > 0 && (
              <span className="inline-flex items-center gap-1.5 text-xs text-blue-600 font-medium">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
                </span>
                {runningRuns} running
              </span>
            )}
            <StatusBadge status={batch.status} />
            <button
              onClick={handleBatchRerun}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary border border-border rounded-lg hover:bg-muted transition-colors"
              title={`Rerun all ${runs.length} runs`}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Rerun all
            </button>

            {/* Rerun metrics (re-evaluate metrics on existing transcripts) */}
            <div className="relative">
              <button
                onClick={() => setRerunMetricsOpen((v) => !v)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-foreground border border-border rounded-lg hover:bg-muted transition-colors"
                title={
                  selectedRunIds.size > 0
                    ? `Rerun metrics on ${selectedRunIds.size} selected run(s)`
                    : `Rerun metrics on all ${runs.length} runs`
                }
              >
                <RotateCcw className="h-3.5 w-3.5 text-muted-foreground" />
                Rerun metrics
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
              {rerunMetricsOpen && rerunMetricMode === "menu" && (
                <div className="absolute right-0 mt-1 w-56 bg-popover border border-border rounded-lg shadow-lg z-20">
                  <div className="px-3 py-1.5 text-[11px] text-muted-foreground border-b border-border">
                    {selectedRunIds.size > 0
                      ? `${selectedRunIds.size} selected run(s)`
                      : `All ${runs.length} runs`}
                  </div>
                  <button
                    onClick={() => handleRerunMetrics(null)}
                    className="w-full text-left px-4 py-2.5 text-xs text-foreground hover:bg-muted/50"
                  >
                    All Metrics
                  </button>
                  <button
                    onClick={() => setRerunMetricMode("select")}
                    disabled={allMetrics.length === 0}
                    className="w-full text-left px-4 py-2.5 text-xs text-foreground hover:bg-muted/50 rounded-b-lg border-t border-border disabled:opacity-50"
                  >
                    Specific Metrics…
                  </button>
                </div>
              )}
              {rerunMetricsOpen && rerunMetricMode === "select" && (
                <div className="absolute right-0 mt-1 w-64 bg-popover border border-border rounded-lg shadow-lg z-20">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                    <span className="text-[11px] font-medium text-muted-foreground">
                      Metrics to rerun
                    </span>
                    <button
                      onClick={() =>
                        setRerunMetricIds(
                          rerunMetricIds.length === allMetrics.length
                            ? []
                            : allMetrics.map((m) => m.id)
                        )
                      }
                      className="text-[11px] text-primary hover:underline"
                    >
                      {rerunMetricIds.length === allMetrics.length ? "Clear" : "All"}
                    </button>
                  </div>
                  <div className="max-h-60 overflow-y-auto py-1">
                    {allMetrics.map((m) => (
                      <label
                        key={m.id}
                        className="flex items-center gap-2 px-3 py-1.5 text-xs text-foreground hover:bg-muted/50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={rerunMetricIds.includes(m.id)}
                          onChange={() => toggleRerunMetric(m.id)}
                          className="h-3.5 w-3.5 rounded border-border"
                        />
                        <span className="truncate">{m.name}</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 border-t border-border">
                    <button
                      onClick={() => setRerunMetricMode("menu")}
                      className="text-[11px] text-muted-foreground hover:text-foreground"
                    >
                      Back
                    </button>
                    <button
                      onClick={() => handleRerunMetrics(rerunMetricIds)}
                      disabled={rerunMetricIds.length === 0}
                      className="ml-auto px-3 py-1.5 text-[11px] font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50"
                    >
                      Rerun {rerunMetricIds.length || ""}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border">
          <div className="text-center">
            <p className="text-2xl font-bold text-foreground">{runs.length}</p>
            <p className="text-xs text-muted-foreground mt-0.5 font-medium uppercase tracking-wide">Total Runs</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-emerald-600">{completedRuns}</p>
            <p className="text-xs text-muted-foreground mt-0.5 font-medium uppercase tracking-wide">Completed</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-red-500">{failedRuns}</p>
            <p className="text-xs text-muted-foreground mt-0.5 font-medium uppercase tracking-wide">Failed</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-foreground">{avgDuration(runs)}</p>
            <p className="text-xs text-muted-foreground mt-0.5 font-medium uppercase tracking-wide">Avg Duration</p>
          </div>
        </div>
      </div>

      {sharedFailureReason && (
        <div className="mb-4 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
          <XCircle className="h-5 w-5 shrink-0 text-red-500 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-red-800">
              {sharedFailureReason.wholeBatch
                ? `All ${sharedFailureReason.count} runs failed for the same reason`
                : `${sharedFailureReason.count} runs failed for the same reason`}
            </p>
            <p className="text-sm text-red-700 mt-0.5 break-words">
              {sharedFailureReason.message}
            </p>
            <p className="text-xs text-red-600/80 mt-1">
              Fix this on the agent, then re-run the batch.
            </p>
          </div>
        </div>
      )}

      {/* Runs Section */}
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-foreground">
            Runs <span className="text-muted-foreground font-normal">({runs.length})</span>
          </h2>
          {selectedRunIds.size > 0 && (
            <button
              onClick={handleBulkRerun}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Play className="h-3.5 w-3.5" />
              Rerun {selectedRunIds.size} selected
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <SearchInput
            placeholder="Search by scenario..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-52"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="h-9 px-3 border border-input rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="running">Running</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>
        </div>
      </div>

      {filteredRuns.length === 0 ? (
        <div className="bg-card rounded-xl border border-border p-12 text-center">
          <p className="text-sm font-medium text-foreground mb-1">No runs found</p>
          <p className="text-sm text-muted-foreground">
            {searchTerm || filterStatus !== "all"
              ? "No runs match your filters"
              : "No runs have been created for this batch yet"}
          </p>
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
          <Table className="min-w-max">
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead className="w-8">
                  <input
                    type="checkbox"
                    className="rounded border-input text-primary focus:ring-ring"
                    checked={filteredRuns.filter((r) => r.status !== "running" && r.status !== "pending").length > 0 &&
                      filteredRuns.filter((r) => r.status !== "running" && r.status !== "pending").every((r) => selectedRunIds.has(r.id))}
                    onChange={toggleSelectAll}
                  />
                </TableHead>
                <TableHead className="w-16">Run ID</TableHead>
                <TableHead>Scenario</TableHead>
                <TableHead>Agent</TableHead>
                <TableHead className="w-24">Type</TableHead>
                <TableHead className="w-28">Status</TableHead>
                <TableHead className="w-28">Call Success</TableHead>
                <TableHead className="w-32">Connection</TableHead>
                <TableHead className="w-24">Duration</TableHead>
                <TableHead className="w-32">Metrics</TableHead>
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagedRuns.map((run, idx) => {
                const metrics = getMetricsSummary(run);
                return (
                  <TableRow
                    key={run.id}
                    className="cursor-pointer group"
                    onClick={() => navigate(`/batch-run/${id}/run/${run.id}`)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {(run.status !== "running" && run.status !== "pending") && (
                        <input
                          type="checkbox"
                          className="rounded border-input text-primary focus:ring-ring"
                          checked={selectedRunIds.has(run.id)}
                          onChange={() => toggleSelectRun(run.id)}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground font-medium font-mono">#{run.id}</span>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-[260px]">
                        <p
                          className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors truncate"
                          title={run.scenario_name || ""}
                        >
                          {run.scenario_name || "—"}
                        </p>
                        {run.summary && (
                          <p className="text-xs text-muted-foreground mt-0.5 truncate" title={run.summary}>
                            {run.summary}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span
                        className="block max-w-[160px] truncate text-sm text-foreground"
                        title={batch?.agent_name || ""}
                      >
                        {batch?.agent_name || "—"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <ConversationTypeBadge mode={run.voice_call_mode} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={run.status} />
                        {run.status === "failed" && run.error_message && (
                          <span className="text-xs text-red-500 truncate max-w-[200px]" title={run.error_message}>{run.error_message}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {run.call_success === true ? (
                        <Badge variant="success">
                          <CheckCircle2 className="h-3 w-3" /> Pass
                        </Badge>
                      ) : run.call_success === false ? (
                        <Badge variant="danger">
                          <XCircle className="h-3 w-3" /> Fail
                        </Badge>
                      ) : (
                        <Badge variant="warning">
                          Evaluating
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {run.voice_call_mode === "text" ? (
                        <span className="text-sm text-muted-foreground">—</span>
                      ) : (() => {
                        const cs = run.call_connection_status || "pending";
                        const variants = {
                          answered: "success",
                          not_answered: "warning",
                          not_connected: "danger",
                          failed: "danger",
                          pending: "muted",
                          skipped: "muted",
                        };
                        return (
                          <Badge variant={variants[cs] || "muted"}>
                            {cs.replace(/_/g, " ")}
                          </Badge>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground whitespace-nowrap">
                        <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        {formatDuration(run.duration)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {metrics ? (
                        <Badge
                          variant={
                            metrics.passed === metrics.total
                              ? "success"
                              : metrics.passed === 0
                              ? "danger"
                              : "warning"
                          }
                        >
                          {metrics.passed === metrics.total ? (
                            <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                          ) : (
                            <XCircle className="h-3.5 w-3.5 shrink-0" />
                          )}
                          {metrics.passed}/{metrics.total} passed
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground flex items-center gap-1 whitespace-nowrap">
                          <BarChart2 className="h-3.5 w-3.5" />
                          {run.status === "pending" || run.status === "running" ? "Pending" : "No metrics"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        {(run.status === "running" || run.status === "pending") ? (
                          <button
                            onClick={(e) => handleStopRun(e, run)}
                            className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-50 transition-colors"
                            title="Stop call"
                          >
                            <StopCircle className="h-4 w-4" />
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={(e) => handleRerun(e, run)}
                              className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-muted transition-colors"
                              title="Rerun"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </button>
                            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <div className="px-4 pb-3">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchRunDetails;
